import { z } from "zod";
import { callDirectionSchema } from "./callDirection.js";
const callTaskEventIdSchema = z.uuid().brand();
const allCallTaskEventTypes = [
  "text_sent",
  "text_received",
  "call_transcript",
  "teammate_call",
  "wait_scheduled",
  "decision",
  "tool_called",
  "opted_out",
  "finished"
];
const callTaskEventTypeSchema = z.enum(allCallTaskEventTypes);
const CONTACT_CALL_TASK_EVENT_TYPES = [
  "text_received",
  "call_transcript",
  "teammate_call",
  "opted_out"
];
const callTaskTeammateCallSpeakerSchema = z.enum([
  "teammate",
  "contact",
  "other"
]);
const CallTaskTool = {
  SendText: "send_text",
  PlaceCall: "place_call",
  Wait: "wait",
  Finish: "finish"
};
const allCallTaskTools = Object.values(CallTaskTool);
const callTaskToolSchema = z.enum(allCallTaskTools);
const CALL_TASK_TOOL_DESCRIPTIONS = {
  [CallTaskTool.SendText]: "Send a text message to the contact.",
  [CallTaskTool.PlaceCall]: "Call the contact.",
  [CallTaskTool.Wait]: "Do nothing until the given time.",
  [CallTaskTool.Finish]: "Ends the task of managing outreach to the contact. Nothing further will be sent or called. Use it when the goal is met, the contact asked you to stop, or there is nothing useful left to do."
};
const callTaskTranscriptSpeakerSchema = z.enum(["agent", "contact"]);
const callTaskEventBodySchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text_sent"),
    body: z.string(),
    /** Why the agent sent it, in its own words. */
    reason: z.string().optional(),
    /** When the agent takes its next turn if no reply comes; absent on a closing text. */
    wait_until: z.string().optional(),
    twilio_message_sid: z.string().optional()
  }),
  z.object({
    type: z.literal("text_received"),
    body: z.string(),
    twilio_message_sid: z.string().optional()
  }),
  z.object({
    type: z.literal("call_transcript"),
    /** Whether we placed the call (`outbound`) or the contact called us. */
    direction: callDirectionSchema,
    utterances: z.array(
      z.object({
        speaker: callTaskTranscriptSpeakerSchema,
        text: z.string()
      })
    ),
    /** Set when the call ended without a transcript reaching us. */
    transcript_unavailable: z.boolean().optional()
  }),
  /**
   * A call a human teammate or an inbound-only voice agent had with the
   * contact, as graded on the source playbook — the reason an auto-enrolled
   * task exists, and any later call the teammate has while the task is open.
   * The row's `recording_id` names the recording, so a second write for the
   * same call is caught before it lands.
   */
  z.object({
    type: z.literal("teammate_call"),
    teammate_name: z.string(),
    /** The graded outcome's label at the time of writing, when one was extracted. */
    outcome_label: z.string().optional(),
    utterances: z.array(
      z.object({
        speaker: callTaskTeammateCallSpeakerSchema,
        text: z.string()
      })
    ),
    /** Set when the transcript was cut to fit; what remains is its end. */
    transcript_truncated: z.boolean().optional(),
    /** Set when the teammate is an AI voice agent rather than a person. */
    is_voice_agent: z.boolean().optional()
  }),
  z.object({
    type: z.literal("wait_scheduled"),
    until: z.string(),
    reason: z.string().optional()
  }),
  /**
   * A turn on which nothing was done: the agent chose a tool the code did not
   * honor (a ceiling was hit, calling hours were closed, a text failed to
   * send), or chose no tool at all (`tool: null`). Kept so the agent's next
   * turn knows what it already tried.
   */
  z.object({
    type: z.literal("decision"),
    tool: callTaskToolSchema.nullable(),
    reason: z.string().optional(),
    refused: z.string()
  }),
  /**
   * The agent used one of its configured tools on a turn — a CRM update, a
   * knowledge-base lookup — as the voice agent does on a call. Recorded so the
   * next turn knows it happened and does not do it again.
   */
  z.object({
    type: z.literal("tool_called"),
    /** The configured tool's id; null for a platform tool such as knowledge retrieval. */
    tool_id: z.string().nullable(),
    name: z.string(),
    /** The arguments as the model gave them, JSON. */
    arguments: z.string(),
    /** What came back, as the timeline shows it; truncated. */
    result: z.string(),
    is_error: z.boolean().optional()
  }),
  z.object({
    type: z.literal("opted_out"),
    source: z.enum(["reply", "twilio_error"]),
    body: z.string().optional()
  }),
  z.object({
    type: z.literal("finished"),
    disposition: z.string(),
    reason: z.string().optional()
  })
]);
export {
  CALL_TASK_TOOL_DESCRIPTIONS,
  CONTACT_CALL_TASK_EVENT_TYPES,
  CallTaskTool,
  allCallTaskEventTypes,
  allCallTaskTools,
  callTaskEventBodySchema,
  callTaskEventIdSchema,
  callTaskEventTypeSchema,
  callTaskTeammateCallSpeakerSchema,
  callTaskToolSchema,
  callTaskTranscriptSpeakerSchema
};
//# sourceMappingURL=callTaskEvent.js.map
